import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Loader2, ShieldCheck, ExternalLink } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { motion } from "framer-motion";
import { useBrand } from "../providers/BootstrapProvider";

// Phase 72 — CMS-driven popup. The `CURRENT_AGREEMENTS_VERSION` export is
// kept for backwards compatibility with any callers that haven't migrated;
// re-prompt is now driven by per-document version comparison on the server.
export const CURRENT_AGREEMENTS_VERSION = "cms-driven";

const DEFAULT_CONFIG = {
  heading: "Welcome to DHDH — Please Review and Accept Our Policies",
  intro_text:
    "Before you get started, please read and agree to the following. By joining DHDH you agree to our Terms of Service, Privacy Policy, and Beta Tester Agreement.",
  agree_checkbox_label:
    "I have read and agree to the Terms of Service, Privacy Policy, and Beta Tester Agreement.",
  verification_checkbox_label:
    "I understand that DHDH is a verified RDH-only community and that providing false credentials may result in immediate account termination.",
  agree_button_text: "I Agree — Take Me to DHDH",
  decline_link_text: "I do not agree — cancel my account",
  documents: [],
};

export default function PolicyAcceptanceModal({ onAccepted }) {
  const brand = useBrand();
  const { logout } = useAuth();
  const [cfg, setCfg] = useState(DEFAULT_CONFIG);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [verifyChecked, setVerifyChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [declining, setDeclining] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/agreement-popup/public`)
      .then((r) => setCfg({ ...DEFAULT_CONFIG, ...r.data }))
      .catch(() => {});
  }, []);

  const bothChecked = agreeChecked && verifyChecked;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bothChecked || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/agreement-popup/accept`, {
        accepted_doc_keys: (cfg.documents || []).map((d) => d.key),
      });
      toast.success("Welcome to the community!");
      onAccepted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record acceptance — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    const ok = window.confirm(
      "This will permanently cancel and delete your DHDH account. Are you sure? This cannot be undone."
    );
    if (!ok) return;
    setDeclining(true);
    try {
      await axios.post(`${API}/account/decline-signup`);
      toast.success("Your account has been cancelled.");
      await logout();
      window.location.href = "/";
    } catch (err) {
      // Fallback to logout if the delete-self endpoint isn't available for this account
      try {
        await logout();
      } finally {
        toast.error(
          err.response?.data?.detail ||
            "We couldn't delete your account automatically — please contact support@dhdh.ca."
        );
        window.location.href = "/";
      }
    } finally {
      setDeclining(false);
    }
  };

  // Non-dismissible: prevent Escape key + outside-click close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") e.preventDefault();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0F172A]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
      data-testid="policy-acceptance-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="policy-popup-heading"
    >
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="bg-[#FDF8F5] w-full sm:max-w-xl rounded-t-3xl sm:rounded-3xl border border-[#E5DFEC] shadow-2xl my-0 sm:my-8 max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 sm:p-8">
          <div className="flex flex-col items-center text-center mb-5">
            {brand?.logo_url ? (
              <img
                src={brand.logo_url}
                alt="DHDH"
                className="w-16 h-16 rounded-full object-cover mb-4"
                data-testid="policy-popup-logo"
              />
            ) : (
              <div className="w-12 h-12 rounded-2xl bg-[#6B4E8A]/10 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-[#6B4E8A]" />
              </div>
            )}
            <h2
              id="policy-popup-heading"
              className="text-2xl font-medium text-[#2A2A2A] leading-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="policy-popup-heading"
            >
              {cfg.heading}
            </h2>
          </div>
          <p className="text-sm text-[#475569] leading-relaxed mb-5 text-center" data-testid="policy-popup-intro">
            {cfg.intro_text}
          </p>

          {/* Document link list */}
          {(cfg.documents || []).length > 0 && (
            <div className="space-y-2 mb-5">
              {cfg.documents.map((d) => (
                <a
                  key={d.key}
                  href={d.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 px-4 py-3 bg-white border border-[#E5DFEC] rounded-xl hover:border-[#6B4E8A]/40 transition-colors"
                  data-testid={`policy-doc-link-${d.key}`}
                >
                  <span className="text-sm font-medium text-[#2A2A2A]">{d.title}</span>
                  <span className="inline-flex items-center gap-1 text-xs text-[#6B4E8A]">
                    Open <ExternalLink className="w-3 h-3" />
                  </span>
                </a>
              ))}
            </div>
          )}

          {/* Primary agreement checkbox */}
          <label
            className="flex items-start gap-3 p-4 bg-white border border-[#E5DFEC] rounded-2xl cursor-pointer hover:border-[#6B4E8A]/40 transition-colors mb-3"
            data-testid="policy-agree-row"
          >
            <Checkbox
              checked={agreeChecked}
              onCheckedChange={(v) => setAgreeChecked(!!v)}
              className="mt-0.5"
              data-testid="policy-agree-checkbox"
            />
            <Label className="text-sm text-[#2A2A2A] cursor-pointer leading-snug block flex-1">
              {cfg.agree_checkbox_label}
            </Label>
          </label>

          {/* Verification credentials checkbox */}
          <label
            className="flex items-start gap-3 p-4 bg-white border border-[#E5DFEC] rounded-2xl cursor-pointer hover:border-[#6B4E8A]/40 transition-colors mb-5"
            data-testid="policy-verify-row"
          >
            <Checkbox
              checked={verifyChecked}
              onCheckedChange={(v) => setVerifyChecked(!!v)}
              className="mt-0.5"
              data-testid="policy-verify-checkbox"
            />
            <Label className="text-sm text-[#2A2A2A] cursor-pointer leading-snug block flex-1">
              {cfg.verification_checkbox_label}
            </Label>
          </label>

          <Button
            type="submit"
            disabled={!bothChecked || submitting}
            className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] disabled:bg-[#CBD5E1] disabled:cursor-not-allowed rounded-full py-6 font-medium"
            data-testid="policy-agree-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
            ) : (
              cfg.agree_button_text
            )}
          </Button>

          <div className="text-center mt-4">
            <button
              type="button"
              onClick={handleDecline}
              disabled={declining}
              className="text-sm text-[#94A3B8] hover:text-[#EF4444] underline disabled:opacity-50"
              data-testid="policy-decline-link"
            >
              {declining ? "Cancelling account…" : cfg.decline_link_text}
            </button>
          </div>
        </div>
      </motion.form>
    </div>
  );
}
